/* eslint-disable sort-exports/sort-exports */
import { atom, WritableAtom } from "jotai";
import { Tool, ToolCall } from "ai";
import * as z from "zod";
import YAML from "json-to-pretty-yaml";
import {
  globalCopilots,
  makeHandoffTool,
  makeTodoTool,
  SerializedTool,
  ToolComponentProps,
} from "@zapier/ai-toolkit/client";
import {
  CopilotInfo,
  createCopilotRegistry,
  Profile,
} from "@zapier/ai-toolkit";
import {
  isDraftModeIncludingTemplatesAtom,
  zslAtom,
  canvasAtom,
  nodesAtom,
  edgesAtom,
  isTourableNode,
  showDebugAtom,
  focusModeAssetAtom,
  iframeFocusModeAtom,
  sessionAtom,
  isAgentsEnabledAtom,
} from "@zapier/canvas-state";
import { ReactFlowInstance } from "@xyflow/react";

import { editCanvasAtom } from "./tools/edit_canvas";
import { networkRequestAtom } from "./tools/network_request";
import { searchToolAtom, searchChunksAtom } from "./tools/search_tool";
import { searchTemplatesToolAtom } from "./tools/search_templates_tool";
import {
  searchRecipesToolAtom,
  useRecipeToolAtom,
  activeRecipeAtom,
} from "./tools/search_recipes_tool";
import { createSystemAtom } from "./tools/create_system";
import { tourToolAtom } from "./tools/tour";
import { explainNodeAtom } from "./tools/explain_node";
import { editSystemAtom } from "./tools/edit_system";
import { focusModePostMessageAtom } from "./tools/focus_mode_post_message";
import {
  setNodeLoadingAtom,
  copilotLoadingNodeIdAtom,
  currentToolMessageAtom,
  setCurrentToolMessageAtom,
} from "./tools/set_node_loading";
import { createRecipeAtom } from "./tools/create_recipe";
import { buildItButtonAtom } from "./tools/build_it_button";
import { continueButtonAtom } from "./tools/continue_button";
import { recipes } from "./tools/search_recipes_tool/recipes";

export { renderAnnotationNodesSetter } from "./tools/tour/renderAnnotationNodesSetter";
export {
  copilotLoadingNodeIdAtom,
  currentToolMessageAtom,
  setCurrentToolMessageAtom,
};
export { getCopilotSuggestions } from "./tools/show_build_mode_suggestions/copilotSuggestions";

export type JotaiToolExecute<T = any> = WritableAtom<
  null,
  [
    {
      args: T;
      conversationId: string;
      reactFlowInstance: ReactFlowInstance;
      submitAndWait: (prompt: string) => Promise<any>;
    },
  ],
  any
>;

export type JotaiTool<T = any> = Omit<Tool, "execute"> & {
  execute: JotaiToolExecute<T>;
  component?: React.FC<ToolComponentProps<any, any>>;
  name: string;
  // For some reason Tool has optional description but ai-toolkit requires it
  description: string;
};

const newBuildModeToolsAtom = atom((get) => {
  const showDebug = get(showDebugAtom);

  const tools: JotaiTool[] = [
    get(focusModePostMessageAtom),
    get(editCanvasAtom),
    get(networkRequestAtom),
    get(searchRecipesToolAtom),
    get(useRecipeToolAtom),
    get(searchTemplatesToolAtom),
    get(searchToolAtom),
    get(tourToolAtom),
    get(explainNodeAtom),
    get(handoffToolAtom),
    get(continueButtonAtom), // Continue button for build mode pauses
    get(setNodeLoadingAtom),
    get(manageTodosToolAtom),
  ];
  if (showDebug) {
    tools.push(get(createRecipeAtom));
  }
  return tools;
});
newBuildModeToolsAtom.debugLabel = "newBuildModeToolsAtom";

const draftModeToolsAtom = atom((get) => [
  get(tourToolAtom),
  get(editSystemAtom),
  get(createSystemAtom),
  get(buildItButtonAtom), // Build it button for draft mode system creation
]);

draftModeToolsAtom.debugLabel = "draftModeToolsAtom";

const buildModeToolsWithRecipeAtom = atom<JotaiTool[]>((get) => {
  const activeRecipe = get(activeRecipeAtom);
  const baseTools = get(newBuildModeToolsAtom);
  const recipe = recipes.find((recipe) => recipe.id === activeRecipe);
  if (activeRecipe && recipe && recipe.toolsAtom) {
    const recipeTools = get(recipe.toolsAtom);
    return [...baseTools, ...recipeTools];
  }
  return baseTools;
});
buildModeToolsWithRecipeAtom.debugLabel = "buildModeToolsWithRecipeAtom";

export const toolsAtom = atom<JotaiTool[]>((get) => {
  const isDraftModeIncludingTemplates = get(isDraftModeIncludingTemplatesAtom);

  if (!isDraftModeIncludingTemplates) {
    return get(buildModeToolsWithRecipeAtom);
  }

  return get(draftModeToolsAtom);
});
toolsAtom.debugLabel = "toolsAtom_SUSPENDS";

/**
 * Handle tool result errors with consistent formatting
 */
const handleToolResultError = (error: any, toolName: string) => {
  const errorMessage = error?.message || "Unknown error occurred";

  return {
    success: true,
    $error: {
      message: errorMessage,
    },
    toolName,
  };
};

export const onToolCallAtom = atom(
  null,
  async (
    get,
    set,
    {
      toolCall,
      conversationId,
      reactFlowInstance,
      submitAndWait,
    }: {
      toolCall: ToolCall<string, any>;
      conversationId: string;
      reactFlowInstance: ReactFlowInstance;
      submitAndWait: (prompt: string) => Promise<any>;
    },
  ) => {
    const tools = get(toolsAtom);
    const tool = tools.find((tool) => tool.name === toolCall.toolName);

    if (tool) {
      // Capture displayMessage if provided (declare outside try/catch for access in catch)
      const displayMessage = toolCall.args?.displayMessage;

      try {
        if (displayMessage) {
          set(setCurrentToolMessageAtom, {
            displayMessage,
          });
        }

        const result = await set(tool.execute, {
          args: toolCall.args,
          conversationId,
          reactFlowInstance,
          submitAndWait,
        });

        if (toolCall.toolName === "handoff") {
          return {
            success: true,
            ...result,
          };
        }

        if (toolCall.toolName === "manage_todos") {
          return result; // Return the todo array directly
        }

        // Extract the text content from the agent-system response format
        if (result && result.content && Array.isArray(result.content)) {
          const textContent = result.content
            .filter((item: any) => item.type === "text")
            .map((item: any) => item.text)
            .join("\n");

          return {
            success: true,
            result: textContent,
          };
        }

        if (result && result.$error) {
          if (displayMessage) {
            set(setCurrentToolMessageAtom, {
              clear: true,
            });
          }
          return handleToolResultError(result.$error, toolCall.toolName);
        }

        // Fallback for unexpected response format
        return {
          success: true,
          result: JSON.stringify(result, null, 2),
        };
      } catch (error) {
        // On error, clear the message and loading state will be handled by the calling code
        if (displayMessage) {
          set(setCurrentToolMessageAtom, { clear: true });
        }

        console.error(
          `Error executing adapted tool ${toolCall.toolName}:`,
          error,
        );
        return {
          success: false,
          $error: {
            message: error instanceof Error ? error.message : String(error),
          },
          toolName: toolCall.toolName,
        };
      }
    }
  },
);
onToolCallAtom.debugLabel = "onToolCallAtom";

export const getBodyCommand = atom(null, (get) => {
  const zsl = get(zslAtom);
  const canvas = get(canvasAtom);
  const nodes = get(nodesAtom);
  const edges = get(edgesAtom);
  const activeRecipe = get(activeRecipeAtom);
  const searchChunks = get(searchChunksAtom);
  const isAgentsEnabled = get(isAgentsEnabledAtom);
  const isDraftModeIncludingTemplates = get(isDraftModeIncludingTemplatesAtom);

  const recipe = activeRecipe
    ? recipes.find((recipe) => recipe.id === activeRecipe)
    : null;

  // Smart function to determine if we should use filtered nodes
  const getNodesForContext = () => {
    // Calculate filtered nodes for tour description
    const filteredNodesForTour = nodes.filter(isTourableNode);
    const hasAnnotationNodes = nodes.some((node) => node.type === "annotation");

    if (hasAnnotationNodes) {
      // When annotation nodes exist, we're in a tour context
      return filteredNodesForTour;
    }

    // No annotation nodes = no tour context, use all nodes
    return nodes;
  };

  const focusModeAsset = get(focusModeAssetAtom);
  const iframe = get(iframeFocusModeAtom);

  const ret = {
    // TODO: remove this once we stop using the old build mode provider
    // we aren't going to change the system message based on which mode you are in
    mode: isDraftModeIncludingTemplates ? "build" : "draft",
    clientTools: serializeTools(get(toolsAtom)),
    additionalData: {
      zsl: zsl || {},
      canvas: canvas || {},
      nodes: getNodesForContext(),
      edges,
      showAgentsNodes: isAgentsEnabled,
      ...(Object.keys(searchChunks).length > 0 && {
        searchChunks: `The following are api's that you have searched for. Use this to help you accomplish the user's request. ${YAML.stringify(searchChunks)}`,
      }),
      ...(activeRecipe && {
        activeRecipe: `The following are additional instructions for the active recipe that you have searched for. Use this to help you accomplish the user's request. ${YAML.stringify(recipe?.fullInstructions)}`,
      }),
      systemMessageVersion: "v4",
      ...(iframe &&
        focusModeAsset && {
          focusModeAsset: `You are now in focus mode working on the ${focusModeAsset.type} with the id ${focusModeAsset.id}. Use this asset to help you accomplish the user's request.`,
        }),
    },
    handoffData: {
      canvasId: canvas?.id,
    },
  };
  return ret;
});
getBodyCommand.debugLabel = "getBodyCommand";

function serializeTools<T>(tools: JotaiTool<T>[]): SerializedTool[] {
  return tools.map(({ name, description, parameters }) => ({
    name,
    description,
    // Use "any" for unrepresentable types. Some schemas (e.g., z.looseObject)
    // use internal transforms that can't be converted to JSON Schema.
    parameters: z.toJSONSchema(parameters, { unrepresentable: "any" }),
  }));
}

export const profileAtom = atom((get) => {
  const session = get(sessionAtom);
  const profile: Profile = {
    email: session?.email || "",
    is_logged_in: Boolean(session?.is_logged_in),
    is_staff: Boolean(session?.is_staff),
    signup: session?.signup || "",
  } as Profile;

  if (session && profile.is_logged_in) {
    profile.email = session.email;
    profile.id = Number(session.id);
    profile.current_account_id = Number(session.current_account_id);
    profile.signup = session.signup;
  }

  return profile;
});
profileAtom.debugLabel = "profileAtom";

const handoffToolAtom = atom<JotaiTool<any>>((get) => {
  const registry = get(getRegistryAtom);
  const profile = get(profileAtom);

  const handoffTool = makeHandoffTool({
    fromCopilotId: "canvas",
    registry,
    profile,
  });

  return {
    ...handoffTool,
    execute: atom(null, async (_get, _set, { args }) => {
      if (handoffTool?.execute) {
        const result = await handoffTool.execute(args);
        return result;
      }
      return null;
    }),
  };
});
handoffToolAtom.debugLabel = "handoffToolAtom";

const manageTodosToolAtom = atom<JotaiTool<any>>(() => {
  const todoTool = makeTodoTool();
  return {
    ...todoTool,
    execute: atom(null, async (_get, _set, { args }) => {
      if (todoTool?.execute) {
        const result = await todoTool.execute(args);
        return result;
      }
      return null;
    }),
  };
});
manageTodosToolAtom.debugLabel = "manageTodosToolAtom";

export const getRegistryAtom = atom(() => {
  return getCanvasCopilotRegistry();
});
getRegistryAtom.debugLabel = "getRegistryAtom";

// Get AI toolkit demos base URL for additional copilots
export const getAiToolkitBaseUrl = () => {
  if (process.env.NODE_ENV === "development") {
    // Use env var for dev port, fallback to 3000
    const aiToolkitPort = process.env.NEXT_PUBLIC_AI_TOOLKIT_PORT || "3000";
    const url = `http://localhost:${aiToolkitPort}`;
    return url;
  }
  return "https://ai-toolkit.vercel.zapier.com";
};

// Additional copilots to extend the global registry
const additionalCopilots = [] satisfies readonly CopilotInfo[];

/**
 * Get Canvas copilot registry that extends the global registry
 * with additional Canvas-specific copilots (search, deep-consult)
 */
const getCanvasCopilotRegistry = () => {
  // Combine global copilots with additional ones
  const allCopilots: CopilotInfo[] = [...globalCopilots, ...additionalCopilots];

  const registry = createCopilotRegistry(allCopilots);

  return registry;
};
/* eslint-enable sort-exports/sort-exports */
